import { Injectable, computed, signal } from '@angular/core';

/** Contact responses shared by the enroll and waitlist forms. */
export interface ContactFormValue {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  tcpaConsent: boolean;
  marketingConsent: boolean;
}

/** Each consent's copy (`innerHTML` of its text), as first rendered; see ContactConsentsComponent. */
export interface ConsentCopy {
  tcpa: string;
  marketing: string;
}

export type ContactFormErrors = Partial<Record<keyof ContactFormValue, string>>;

export interface ContactValidationOptions {
  phoneRequired: boolean;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const INITIAL_CONTACT: ContactFormValue = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  tcpaConsent: true,
  marketingConsent: true,
};

/** Formats digits as a US phone number while typing, e.g. "(214) 216-6542". */
export function formatPhone(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 10);
  if (d.length === 0) return '';
  if (d.length < 4) return `(${d}`;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export function validateContact(value: ContactFormValue, { phoneRequired }: ContactValidationOptions): ContactFormErrors {
  const errors: ContactFormErrors = {};
  if (!value.firstName.trim()) errors.firstName = 'Enter your first name.';
  if (!value.lastName.trim()) errors.lastName = 'Enter your last name.';

  const email = value.email.trim();
  if (!email) errors.email = 'Enter your email.';
  else if (!EMAIL_PATTERN.test(email)) errors.email = 'Enter a valid email.';

  const phoneDigits = value.phone.replace(/\D/g, '');
  if (!phoneDigits && phoneRequired) errors.phone = 'Enter your phone number.';
  else if (phoneDigits && phoneDigits.length !== 10) errors.phone = 'Enter a 10-digit US phone number.';

  if (!value.tcpaConsent) errors.tcpaConsent = 'This consent is required to continue.';
  return errors;
}

/**
 * Holds the contact responses so either form (enroll or waitlist) can read, edit and submit them
 * to its own endpoint without re-entering anything.
 */
@Injectable({ providedIn: 'root' })
export class ContactFormStore {
  readonly contact = signal<ContactFormValue>({ ...INITIAL_CONTACT });

  private readonly copy = signal<ConsentCopy | null>(null);
  readonly consentCopy = this.copy.asReadonly();
  /**
   * The TCPA `consentedTo`, sent verbatim for legal defensibility: the saved TCPA copy always, plus the marketing
   * copy when checked. Built only from the saved copy, never the live page.
   */
  readonly consentedTo = computed(() => {
    const copy = this.copy();
    if (!copy) return '';
    return this.contact().marketingConsent ? `<p>${copy.tcpa}</p><p>${copy.marketing}</p>` : `<p>${copy.tcpa}</p>`;
  });

  /** Saves the consent copy the first time it renders; later renders keep it (the copy doesn't change). */
  saveConsentCopy(copy: ConsentCopy): void {
    if (!this.copy()) this.copy.set(copy);
  }

  reset(): void {
    this.contact.set({ ...INITIAL_CONTACT });
  }
}
